/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 *
 * Game developers wire their rendering engine inside mountGame() — replace
 * the placeholder with PixiJS, Babylon.js, or DOM rendering.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { mountGame } from './engine/bootstrap';
import { mountAdmin } from './engine/game/admin-page';
import { renderLanding } from './engine/landing/landing-screen';
import { renderLegalPage } from './engine/landing/legal-page';
import { attachVerifyRedirect } from './engine/verify-redirect';
import { attachForgotMessages, attachLoginMessages, attachRegisterMessages } from './engine/auth-form-messages';
import { captureReferralCode } from './engine/game/referral-api';
import { installIdleLogout } from './engine/game/idle-logout';
import { installSessionKeepalive } from './engine/game/session-keepalive';

// Referral links (`?ref=CODE`) can land on any pre-auth screen (landing,
// /login, /register) — capture + strip before the core auth UI ever renders;
// the actual claim happens later, once a session exists (see HeroScreen).
captureReferralCode();

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;
let lastPathname = location.pathname;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  lastPathname = location.pathname;
  render();
}

/** Combine several teardown fns into one. */
const combine = (...fns: Array<(() => void) | null | undefined>) => () => fns.forEach((f) => f?.());

/**
 * Floating "back to the landing site" control shown over the core auth screens
 * (which the game can't edit). Lets a visitor who hit Log In / Sign Up bail back
 * to the marketing landing at `/`. Returns a teardown that removes it.
 */
function addBackToLanding(): () => void {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Back to home');
  btn.innerHTML = '<span aria-hidden="true">&#8249;</span> Back';
  btn.style.cssText = [
    'position:fixed', 'top:20px', 'left:20px', 'z-index:1000',
    'display:inline-flex', 'align-items:center', 'gap:7px',
    'font:700 12px/1 "Courier Prime", "Courier New", monospace',
    'letter-spacing:.14em', 'text-transform:uppercase',
    'color:#e3c878', 'background:rgba(20,18,26,.72)', '-webkit-backdrop-filter:blur(6px)', 'backdrop-filter:blur(6px)',
    'border:1px solid rgba(200,162,76,.45)', 'border-radius:999px',
    'padding:10px 18px', 'cursor:pointer', 'transition:background .2s, transform .15s',
  ].join(';');
  btn.onmouseenter = () => { btn.style.background = 'rgba(200,162,76,.16)'; btn.style.transform = 'translateY(-1px)'; };
  btn.onmouseleave = () => { btn.style.background = 'rgba(20,18,26,.72)'; btn.style.transform = 'none'; };
  btn.onclick = () => navigate('/');
  document.body.appendChild(btn);
  return () => btn.remove();
}

/**
 * The token was just spent server-side, so any cached user still says
 * emailVerified:false — /play would bounce it straight to /check-email.
 * Re-bootstrap first so the store reflects the verification.
 */
function addVerifyRedirect(): () => void {
  return attachVerifyRedirect(document, async () => {
    const user = await bootstrapSession();
    navigate(user?.emailVerified ? '/play' : '/login');
  });
}

function render() {
  teardown?.();
  teardown = null;

  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = combine(renderLogin(app, afterAuth), attachLoginMessages(app), addBackToLanding());
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = combine(renderRegister(app, afterAuth), attachRegisterMessages(app), addBackToLanding());
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = combine(renderForgotPassword(app), attachForgotMessages(app), addBackToLanding());
      break;
    case '/reset-password':
      teardown = renderResetPassword(app, params.get('token') ?? '', () => navigate('/login'));
      break;
    case '/verify-email':
      teardown = combine(renderVerifyEmail(app, params.get('token') ?? ''), addVerifyRedirect());
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); });
      break;
    case '/play':
    case '/':
      if (!user) {
        // First-time / logged-out visitors see the marketing landing at the root;
        // its "Play Free" CTAs hand off to the login screen.
        if (path === '/') { teardown = renderLanding(app, () => navigate('/login')); break; }
        navigate('/login');
        return;
      }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      connectSocket();
      {
        const endSession = () => { logout(); disconnectSocket(); navigate('/login'); };
        // Product rule: 10 minutes without input ends the session (and with it,
        // any chance of coming back to hours-stale in-memory game state).
        teardown = combine(
          mountGame(app, user, endSession),
          installIdleLogout(endSession),
          // JWT_REFRESH_TTL is 10m so a closed tab dies on its own; this keeps
          // the token rotating for a player who is present but not clicking.
          installSessionKeepalive(),
        );
      }
      break;
    case '/admin-panel/sandbox':
    case '/admin-panel':
      if (!user) { navigate('/login'); return; }
      if (user.role !== 'ADMIN' && user.role !== 'KYC_ADMIN') { navigate('/play'); return; }
      teardown = mountAdmin(app, user);
      break;
    default:
      // /legal/:slug — standalone CMS-driven legal document (Terms, Privacy, Cookies)
      if (path.startsWith('/legal/')) {
        teardown = renderLegalPage(app, decodeURIComponent(path.slice('/legal/'.length)), () => navigate('/'));
        break;
      }
      navigate('/login');
  }
}

window.addEventListener('popstate', () => {
  if (location.pathname === lastPathname && location.hash) return;
  lastPathname = location.pathname;
  render();
});

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  // Static files (news.html etc.) are served as real pages, not SPA routes —
  // let the browser do a full navigation instead of falling through to /login.
  if (/\.[a-z0-9]+$/i.test(a.pathname)) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();

  await bootstrapSession();
  render();

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/login');
  });
})();

// NOTE: no beforeunload teardown. Browsers fire beforeunload for navigations
// that never happen (mailto: links hand off to the mail client and stay on the
// page) — tearing down there left a blank, dead SPA. On a real unload the
// browser discards the page anyway; nothing needs manual cleanup.
